import React from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { IconPointFilled } from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import {
  AttachmentDto,
  DesignModelDto,
  DesignModelRequestDto,
  JobDto,
} from 'src/api/FiretrolApiClient';
import {
  useResolveAttachmentUrl,
  useUploadTempAttachment,
} from 'src/api/queries/attachments/useAttachmentMutations';
import { useDesignModel } from 'src/api/queries/designModels/useDesignModel';
import { isConversionTerminal } from 'src/api/queries/designModels/useDesignModels';
import { useDesignModelLaborAccounts } from 'src/api/queries/designModels/useDesignModelLaborAccounts';
import {
  useCreateDesignModel,
  useDeleteDesignModel,
  useReprocessDesignModel,
  useUpdateDesignModel,
} from 'src/api/queries/designModels/useDesignModelMutations';
import { queryKeys } from 'src/api/queries/queryKeys';
import { useWorkflowFormValidation } from 'src/app/workflows/useWorkflowFormValidation';
import { Alert } from 'src/design/atoms/Alert';
import { Group } from 'src/design/atoms/Group';
import { PaperStack } from 'src/design/atoms/PaperStack';
import { SaveButton } from 'src/design/atoms/SaveButton';
import { Select } from 'src/design/atoms/Select';
import { SimpleGrid } from 'src/design/atoms/SimpleGrid';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { TextInput } from 'src/design/atoms/TextInput';
import { Textarea } from 'src/design/atoms/Textarea';
import { Title } from 'src/design/atoms/Title';
import { applyValidationErrors, getErrorMessage, logger } from 'src/utils';
import { DesignModelActionBar } from 'src/routes/Jobs/DesignModels/components/DesignModelActionBar';
import { DesignModelFileSlot } from 'src/routes/Jobs/DesignModels/components/DesignModelFileSlot';
import { DesignModelStatusStrip } from 'src/routes/Jobs/DesignModels/components/DesignModelStatusStrip';
import {
  DesignModelFileKind,
  FILE_KINDS,
  FILE_REQUIRED_MESSAGES,
  IDesignModelFileEdits,
  hasActiveFile,
  isFileKind,
  savedFilesOf,
  stageFile,
  stagedFileOf,
  toFileKinds,
  unstageFile,
} from 'src/routes/Jobs/DesignModels/designModelFiles';
import { useBeforeUnloadGuard } from 'src/routes/Jobs/DesignModels/hooks/useBeforeUnloadGuard';
import { getReprocessBlockReason } from 'src/routes/Jobs/DesignModels/reprocessGate';

export interface IDesignModelFormValues extends IDesignModelFileEdits {
  id?: string;
  name: string;
  description: string;
  wbsAccountId: string | null;
  /** Validation-only keys: the files themselves live in the attachment lists. */
  ifc?: undefined;
  fab?: undefined;
  cad?: undefined;
}

const FIELD_LABELS: Record<string, string> = {
  name: 'Model Name',
  ifc: 'IFC File',
  fab: 'FAB File',
  cad: 'CAD File',
};

const SERVER_FIELDS = ['name', 'description', 'wbsAccountId', ...FILE_KINDS];
const NO_SERVER_FIELD_ERRORS: Record<string, string> = {};

const fileRule = (kind: DesignModelFileKind) =>
  Yup.mixed().test(
    `${kind}-required`,
    FILE_REQUIRED_MESSAGES[kind],
    (_v, ctx) => hasActiveFile(ctx.parent as IDesignModelFormValues, kind),
  );

const validationSchema = Yup.object().shape({
  name: Yup.string().trim().required('Model name is required.'),
  ifc: fileRule('ifc'),
  fab: fileRule('fab'),
  cad: fileRule('cad'),
});

/**
 * The WBS root picker locks once a root has been persisted for a FAB-driven
 * model. Keying off the persisted root (not the live form value) means picking
 * a value doesn't immediately re-lock the Select before it's saved, and models
 * with FAB data but no root yet remain editable.
 */
export function isWbsRootLocked(
  hasFabData: boolean,
  persistedWbsAccountId: string | null | undefined,
): boolean {
  return hasFabData && !!persistedWbsAccountId;
}

export const toFormValues = (
  model: DesignModelDto,
): IDesignModelFormValues => ({
  id: model.id,
  name: model.name ?? '',
  description: model.description ?? '',
  wbsAccountId: model.wbsAccountId ?? null,
  attachment: model.attachment ?? [],
  attachmentsToAdd: [],
  attachmentsToRemove: [],
});

export const countUnsavedChanges = (
  initial: IDesignModelFormValues,
  values: IDesignModelFormValues,
): number =>
  (['name', 'description', 'wbsAccountId'] as const).filter(
    (field) => (initial[field] ?? '') !== (values[field] ?? ''),
  ).length + FILE_KINDS.filter((kind) => !!stagedFileOf(values, kind)).length;

export interface IDesignModelFormProps {
  initialValues: DesignModelDto;
  selectedJob: JobDto;
  onCreated?: (designModel: DesignModelDto) => void;
  onDeleted?: () => void;
  onCancel: () => void;
  /** Read-only results shown after the form sections, above Save. */
  diagnostics?: React.ReactNode;
}

export const DesignModelForm: React.FC<IDesignModelFormProps> = ({
  initialValues: model,
  selectedJob,
  onCreated,
  onDeleted,
  onCancel,
  diagnostics,
}) => {
  const qc = useQueryClient();
  const isCreate = !model.id;
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [reprocessError, setReprocessError] = React.useState<string | null>(
    null,
  );
  const [slotErrors, setSlotErrors] = React.useState<
    Partial<Record<DesignModelFileKind, string>>
  >({});
  const [uploadingKinds, setUploadingKinds] = React.useState<
    Partial<Record<DesignModelFileKind, boolean>>
  >({});

  const { data: laborAccounts } = useDesignModelLaborAccounts(selectedJob.id);
  const uploadTempFile = useUploadTempAttachment('design-models');
  const resolveAttachmentUrl = useResolveAttachmentUrl();
  const createDesignModel = useCreateDesignModel(selectedJob.id);
  const updateDesignModel = useUpdateDesignModel(selectedJob.id);
  const deleteDesignModel = useDeleteDesignModel(selectedJob.id);
  const reprocessDesignModel = useReprocessDesignModel(selectedJob.id);
  const saving = createDesignModel.isPending || updateDesignModel.isPending;

  const detailQuery = useDesignModel(model.id, { poll: true });
  const server = detailQuery.data ?? model;
  const missing = toFileKinds(server.missingFileTypes);
  const processing = !isCreate && server.conversionStatus === 'Processing';

  const formik = useFormik<IDesignModelFormValues>({
    initialValues: toFormValues(model),
    validationSchema,
    validateOnMount: true,
    onSubmit: async () => {},
  });
  const { values, setFieldValue } = formik;
  const { passesValidation, fieldError } =
    useWorkflowFormValidation<IDesignModelFormValues>(
      formik,
      NO_SERVER_FIELD_ERRORS,
      FIELD_LABELS,
    );

  const unsavedCount = countUnsavedChanges(formik.initialValues, values);
  const dirty = unsavedCount > 0;
  const uploading = Object.values(uploadingKinds).some(Boolean);
  useBeforeUnloadGuard(dirty || uploading);

  const dirtyRef = React.useRef(dirty);
  dirtyRef.current = dirty;
  const settledStatusRef = React.useRef(model.conversionStatus);
  React.useEffect(() => {
    const latest = detailQuery.data;
    if (!latest?.id) return;
    const previous = settledStatusRef.current;
    settledStatusRef.current = latest.conversionStatus;
    if (
      previous === latest.conversionStatus ||
      !isConversionTerminal(latest.conversionStatus)
    )
      return;
    qc.invalidateQueries({
      queryKey: queryKeys.designModels.grouping(latest.id),
    });
    // Status, error, missing files and parsed data are read from the query,
    // so an edited form only has to keep its own values.
    if (!dirtyRef.current) formik.resetForm({ values: toFormValues(latest) });
  }, [detailQuery.data, qc]);

  const onUploadingChange = React.useCallback(
    (kind: DesignModelFileKind, isUploading: boolean) =>
      setUploadingKinds((prev) =>
        !!prev[kind] === isUploading ? prev : { ...prev, [kind]: isUploading },
      ),
    [],
  );

  const clearSlotError = (kind: DesignModelFileKind) =>
    setSlotErrors(({ [kind]: _cleared, ...rest }) => rest);

  const handleStage = (kind: DesignModelFileKind, file: AttachmentDto) => {
    clearSlotError(kind);
    formik.setValues((v) => ({ ...v, ...stageFile(v, kind, file) }));
  };

  const handleUnstage = (kind: DesignModelFileKind) => {
    clearSlotError(kind);
    formik.setValues((v) => ({ ...v, ...unstageFile(v, kind) }));
  };

  const handleSave = async () => {
    if (saving || uploading) return;
    if (!(await passesValidation())) return;
    setSaveError(null);
    setSlotErrors({});
    const request: DesignModelRequestDto = {
      id: values.id,
      name: values.name.trim(),
      description: values.description || undefined,
      jobId: selectedJob.id,
      fileSize: 0,
      wbsAccountId: values.wbsAccountId ?? undefined,
      attachmentsToAdd: values.attachmentsToAdd,
      attachmentsToRemove: values.attachmentsToRemove,
    };
    try {
      const result = values.id
        ? await updateDesignModel.mutateAsync({ id: values.id, request })
        : await createDesignModel.mutateAsync(request);
      setReprocessError(null);
      formik.resetForm({ values: toFormValues(result) });
      if (!values.id) onCreated?.(result);
    } catch (err) {
      logger.error('Failed to save design model', { err });
      const slot: Partial<Record<DesignModelFileKind, string>> = {};
      const { applied, unmatchedMessages } = applyValidationErrors(
        err,
        SERVER_FIELDS,
        (field, message) => {
          if (isFileKind(field)) {
            slot[field] = message;
          } else {
            formik.setFieldTouched(field, true, false);
            formik.setFieldError(field, message);
          }
        },
      );
      setSlotErrors(slot);
      setSaveError(
        !applied
          ? getErrorMessage(err, 'The design model could not be saved.')
          : unmatchedMessages.length > 0
          ? unmatchedMessages.join(' ')
          : null,
      );
    }
  };

  const handleReprocess = async () => {
    if (!model.id) return;
    setReprocessError(null);
    try {
      await reprocessDesignModel.mutateAsync(model.id);
    } catch (err) {
      // The mutation's onSettled invalidation refetches the detail, so the
      // strip and slots show the server's view of why it was refused.
      logger.error('Failed to reprocess design model', { err });
      setReprocessError(
        getErrorMessage(err, 'Processing could not be started.'),
      );
    }
  };

  const handleDelete = async () => {
    if (!model.id) return;
    await deleteDesignModel.mutateAsync(model.id);
    onDeleted?.();
  };

  const savedOf = (kind: DesignModelFileKind) =>
    savedFilesOf(values.attachment, kind)[0];
  const savedFileNames = Object.fromEntries(
    FILE_KINDS.map((kind) => [kind, savedOf(kind)?.name]),
  ) as Partial<Record<DesignModelFileKind, string>>;

  const reprocessBlock = getReprocessBlockReason({
    isCreate,
    pending: saving || reprocessDesignModel.isPending,
    conversionStatus: server.conversionStatus,
    dirty,
    uploading,
    missing,
    hasSavedIfc: !!savedOf('ifc'),
  });

  const lockedReason = saving
    ? 'Wait for the save to finish.'
    : processing
    ? 'File changes are locked while the model is processing.'
    : null;

  const hasFabData = !!server.fabData;
  const wbsPickerDisabled = isWbsRootLocked(hasFabData, server.wbsAccountId);
  const hasStagedFiles = FILE_KINDS.some(
    (kind) => !!stagedFileOf(values, kind),
  );

  const unsavedText = `${unsavedCount} unsaved change${
    unsavedCount === 1 ? '' : 's'
  }`;
  const statusText = uploading
    ? 'Waiting for upload to finish'
    : unsavedCount === 0
    ? null
    : !isCreate && hasStagedFiles
    ? `${unsavedText}. Saving reprocesses the model.`
    : unsavedText;

  return (
    <Stack gap="md">
      <DesignModelActionBar
        isCreate={isCreate}
        modelName={server.name}
        reprocessBlock={reprocessBlock}
        savedFileNames={savedFileNames}
        onReprocess={handleReprocess}
        onDelete={handleDelete}
        dirty={dirty}
        onCancel={onCancel}
      />

      {!isCreate && (
        <DesignModelStatusStrip
          conversionStatus={server.conversionStatus}
          conversionError={server.conversionError}
          missing={missing}
          allFilesSaved={FILE_KINDS.every((kind) => !!savedOf(kind))}
          refreshFailed={detailQuery.isError}
          refreshing={detailQuery.isFetching}
          onRetry={() => detailQuery.refetch()}
          reprocessError={reprocessError}
        />
      )}

      <PaperStack>
        <Title order={4}>Details</Title>
        <SimpleGrid cols={{ base: 1, md: 2 }}>
          <TextInput
            label="Model Name"
            placeholder="Enter model name"
            name="name"
            value={values.name}
            onChange={(e) => setFieldValue('name', e.target.value)}
            onBlur={formik.handleBlur}
            error={fieldError('name')}
            required
          />
          <Select
            label="WBS Root Account"
            description={
              wbsPickerDisabled
                ? 'Locked — WBS root cannot be changed after FAB processing.'
                : hasFabData
                ? 'Select a labor account. WBS accounts will be built from the existing FAB data when you save.'
                : 'Select a labor account under which FAB-driven WBS accounts will be created.'
            }
            inputWrapperOrder={['label', 'input', 'description', 'error']}
            placeholder="Select WBS root account"
            data={(laborAccounts ?? []).map((a) => ({
              value: a.id,
              label: `${a.name} (${a.code})`,
            }))}
            value={values.wbsAccountId}
            onChange={(value) => setFieldValue('wbsAccountId', value)}
            disabled={wbsPickerDisabled}
            clearable
          />
        </SimpleGrid>
        <Textarea
          label="Description"
          value={values.description}
          onChange={(e) => setFieldValue('description', e.target.value)}
          rows={3}
        />
      </PaperStack>

      <PaperStack>
        <Title order={4}>Source files</Title>
        {FILE_KINDS.map((kind) => (
          <DesignModelFileSlot
            key={kind}
            kind={kind}
            saved={savedOf(kind)}
            staged={stagedFileOf(values, kind)}
            missing={missing.includes(kind)}
            lockedReason={lockedReason}
            error={slotErrors[kind] ?? fieldError(kind)}
            uploadFile={(file) =>
              uploadTempFile.mutateAsync({ data: file, fileName: file.name })
            }
            downloadFile={(id) => resolveAttachmentUrl.mutateAsync(id)}
            onStage={handleStage}
            onUnstage={handleUnstage}
            onUploadingChange={onUploadingChange}
          />
        ))}
      </PaperStack>

      {diagnostics}

      {saveError && (
        <Alert color="red" title="Could not save">
          {saveError}
        </Alert>
      )}

      <Group>
        <SaveButton onClick={handleSave} loading={saving} disabled={uploading}>
          {isCreate ? 'Create' : 'Save'}
        </SaveButton>
        {statusText && (
          <Group gap={4} wrap="nowrap">
            <IconPointFilled size={14} color="var(--mantine-color-yellow-6)" />
            <Text size="sm" c="dimmed">
              {statusText}
            </Text>
          </Group>
        )}
      </Group>
    </Stack>
  );
};
